import React, { useState, useEffect } from "react";
import { api_url } from "../../constant/constant";
import { showToast } from "../../Components/ShowToast/ToastNotification";
import { useTranslation } from "react-i18next";
import { useNavigate,useLocation } from "react-router-dom";

const typeOptions = [
  "By Awareness Score Percentage",
  "By Department",
  "By Campaign",
  "Did Not Meet Success Score",
  "Opened Email Did Not Finish Lesson",
  "Completed Lesson Did Not Take Exam",
  "Failed Users",
];

const operatorOptions = [">", "<", "=", "<=", ">="];

const Attachbyuserfilter = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const LearningManager_id = searchParams.get("LearningManager_id");

  const [loading, setLoading] = useState(false);
  const [relation, setRelation] = useState("AND");
  const [filters, setFilters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // 🔹 Fetch Departments
  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        const token = localStorage.getItem("userToken");
        const response = await fetch(`${api_url}/admin/main/departments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch departments");
        const result = await response.json();
        setDepartments(result.data);
      } catch (error) {
        showToast(t("fetcherror"), "error");
      }
    };
    fetchDepartment();
  }, [t]);

  // 🔹 Fetch Campaigns with Pagination
  useEffect(() => {
    const fetchAllCampaigns = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("userToken");
        let allCampaigns = [];
        let page = 1;
        let totalPages = 1;

        do {
          const response = await fetch(`${api_url}/learning-campaigns?page=${page}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          const result = await response.json();
          const campaignData = result.learning_campaigns?.data || [];
          allCampaigns = [...allCampaigns, ...campaignData];

          totalPages = result.learning_campaigns?.last_page || 1;
          page++;
        } while (page <= totalPages);

        setCampaigns(allCampaigns);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        showToast(t("errorfetchingdata"), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAllCampaigns();
  }, [t]);

  // 🔹 Add/Remove/Update Filters
  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      { id: Date.now(), type: "", operator: "", value: "" },
    ]);
  };

  const removeFilter = (id) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleFilterChange = (id, name, value) => {
    setFilters((prev) =>
      prev.map((f) => {
        if (f.id === id) {
          // If type changed to By Campaign, set value = LearningManager_id by default
          if (name === "type" && value === "By Campaign" && !f.value) {
            return { ...f, type: value, value: LearningManager_id || "" };
          }
          return { ...f, [name]: value };
        }
        return f;
      })
    );
  };

  const availableTypes = (currentId) => {
    const selectedTypes = filters
      .filter((f) => f.id !== currentId)
      .map((f) => f.type);
    return typeOptions.filter((type) => !selectedTypes.includes(type));
  };

  const mapTypeToApi = (type) => {
    switch (type) {
      case "By Awareness Score Percentage":
        return "by_awareness_score_percentage";
      case "By Department":
        return "by_department";
      case "By Campaign":
        return "by_campaign";
      case "Did Not Meet Success Score":
        return "did_not_meet_success_score";
      case "Opened Email Did Not Finish Lesson":
        return "opened_email_did_not_finish_lesson";
      case "Completed Lesson Did Not Take Exam":
        return "completed_lesson_did_not_take_exam";
      case "Failed Users":
        return "failed_users";
      default:
        return "";
    }
  };

  const getAvailableCampaigns = (currentFilterValue) => {
    const selectedCampaignIds = filters
      .filter((f) => f.type === "By Campaign" && f.value)
      .map((f) => Number(f.value));

    return campaigns.filter(
      (c) =>
        c.id === Number(currentFilterValue) ||
        (!selectedCampaignIds.includes(c.id) && c.id !== Number(LearningManager_id))
    );
  };

  // 🔹 Prepare Payload
  const payload = {
    relation,
    filters: filters
      .filter((f) => f.type)
      .map((f) => ({
        type: mapTypeToApi(f.type),
        ...(f.operator ? { operator: f.operator } : {}),
        ...(f.value ? { value: f.value } : {}),
      })),
  };

  // 🔹 Submit API
  const handleSubmit = async () => {
    if (!LearningManager_id) {
      showToast(t("InvalidLearningManagerId"), "error");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${api_url}/bulk-enrollment/learning/${LearningManager_id}/enroll-users`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        showToast(t("FilterAppliedSuccessfully"), "success");
        console.log("API Response:", result);
        navigate(-1);

      } else {
        showToast(result.message || t("SomethingWentWrong"), "error");
      }
    } catch (error) {
      showToast(t("NetworkError"), "error");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 JSX
  return (
    <div className="container mt-4">
      <h3 className="mb-4">{t("AddUsersUsingQuery")}</h3>

      {/* Relation Dropdown */}
      <div className="mb-3">
        <label className="form-label">{t("Relation(AND/OR)")}</label>
        <select
          className="form-select"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
        >
          <option value="AND">{t("AND")}</option>
          <option value="OR">{t("OR")}</option>
        </select>
      </div>

      {/* Filters */}
      {filters.map((filter) => (
        <div key={filter.id} className="border p-3 mb-3 rounded">
          {/* Type */}
          <div className="mb-2">
            <label className="form-label">{t("Type")}</label>
            <select
              name="type"
              className="form-select"
              value={filter.type}
              onChange={(e) =>
                handleFilterChange(filter.id, "type", e.target.value)
              }
            >
              <option value="">{t("SelectType")}</option>
              {availableTypes(filter.id).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Awareness Score */}
          {filter.type === "By Awareness Score Percentage" && (
            <>
              <div className="mb-2">
                <label className="form-label">{t("Operator")}</label>
                <select
                  name="operator"
                  className="form-select"
                  value={filter.operator}
                  onChange={(e) =>
                    handleFilterChange(filter.id, "operator", e.target.value)
                  }
                >
                  <option value="">{t("SelectOperator")}</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">{t("Value")}</label>
                <input
                  type="range"
                  className="form-range"
                  min="0"
                  max="100"
                  step="1"
                  value={filter.value || 0}
                  onChange={(e) =>
                    handleFilterChange(filter.id, "value", Number(e.target.value))
                  }
                />
                <p>Score: {filter.value || 0}%</p>
              </div>
            </>
          )}

          {/* Department */}
          {filter.type === "By Department" && (
            <div className="mb-2">
              <label className="form-label">{t("Department")}</label>
              <select
                name="value"
                className="form-select"
                value={filter.value}
                onChange={(e) =>
                  handleFilterChange(filter.id, "value", e.target.value)
                }
              >
                <option value="">{t("SelectDepartment")}</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Campaign */}
          {filter.type === "By Campaign" && (
            <div className="mb-2">
              <label className="form-label">{t("Campaign")}</label>
              <select
                name="value"
                className="form-select"
                value={filter.value}
                onChange={(e) =>
                  handleFilterChange(filter.id, "value", e.target.value)
                }
              >
                <option value="">{t("SelectCampaign")}</option>
                {getAvailableCampaigns(filter.value).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title_en || `Campaign ${c.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Static Types */}
          {[
            "Did Not Meet Success Score",
            "Opened Email Did Not Finish Lesson",
            "Completed Lesson Did Not Take Exam",
            "Failed Users",
          ].includes(filter.type) && <p className="text-muted"></p>}

          <button
            type="button"
            className="btn btn-danger btn-sm mt-2"
            onClick={() => removeFilter(filter.id)}
          >
            {t("Remove")}
          </button>
        </div>
      ))}

      {/* Add Filter Button */}
      <button type="button" className="btn btn-primary" onClick={addFilter}>
        {t("AddFilter")}
      </button>

      {/* Submit Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          className="btn btn-success"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? t("Loading...") : t("Submit")}
        </button>
      </div>
    </div>
  );
};

export default Attachbyuserfilter;
